import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Sparkles } from 'lucide-react';
import { Badge, Button, Card, CardContent, cn } from '@mlm/ui';
import { useBrand } from '../storefront/lib/hooks';
import { productImageUrl } from '../lib/api';
import { fmtCurrency } from '../lib/format';

/**
 * Catalog card + image for the activation-kit grid (`KitGrid`), its only
 * importer today. It once also served the authed /store (now a redirect to
 * /storefront) and the public /shop, which renders the storefront's own card.
 * The normalized `ProductCardView` keeps the card decoupled from the server
 * product shape.
 */

export type ProductCardView = {
  id: string;
  name: string;
  sku: string;
  description: string | null;
  images: string[];
  isKit: boolean;
  featured: boolean;
  pv: string;
  /** Price shown prominently (member-effective on /store, base on /shop). */
  price: string;
  /** Original price, struck through. Only rendered when a discount applies. */
  priceBefore?: string | null;
  /** > 0 renders the −X% discount badge (authed store only). */
  discountPct?: number;
};

/**
 * How far from square a source may be before we stop cropping it. The frame is
 * a fixed square (grid rows must line up), so `object-cover` scales the image to
 * the frame and slices whatever doesn't fit — fine for a product photo, ruinous
 * for a wide infographic, which loses everything but its centre band and reads
 * as blown-up and cut off. Outside this band we show the image whole against the
 * muted wash instead. Measured from the decoded image, so a differently-shaped
 * upload needs no code change. Same reasoning as the UPMI gallery fix
 * (`tenants/upmi/storefront/gallery.tsx`), applied to a fixed frame.
 */
const RATIO_MIN = 3 / 4;
const RATIO_MAX = 4 / 3;

/** Square product image with a graceful placeholder when the product has none. */
export function ProductImage({
  product,
  className,
}: {
  product: { id: string; name: string; images?: string[] };
  className?: string;
}) {
  const [errored, setErrored] = useState(false);
  const [contain, setContain] = useState(false);
  const hasImage = (product.images?.length ?? 0) > 0 && !errored;
  return (
    <div className={cn('relative bg-muted overflow-hidden', className)}>
      {hasImage ? (
        <img
          src={productImageUrl(product.id, 0)}
          alt={product.name}
          loading="lazy"
          className={cn('h-full w-full', contain ? 'object-contain' : 'object-cover')}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (!img.naturalWidth || !img.naturalHeight) return;
            const ratio = img.naturalWidth / img.naturalHeight;
            setContain(ratio < RATIO_MIN || ratio > RATIO_MAX);
          }}
          onError={() => setErrored(true)}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground/50">
          <ShoppingCart className="h-8 w-8" />
          <span className="line-clamp-2 text-center text-xs leading-tight">{product.name}</span>
        </div>
      )}
    </div>
  );
}

export function ProductCard({
  view,
  basePath,
  inCart,
  pending,
  onAdd,
  onBuyNow,
  chooseVariationHref,
}: {
  view: ProductCardView;
  /** Detail-link base: `/store` (authed) or `/shop` (public). */
  basePath: string;
  inCart: number;
  pending?: boolean;
  onAdd: () => void;
  /**
   * Set for a product that HAS variations. This portal's cart is keyed by
   * product id alone and carries no variation, so adding one here would place an
   * order the server now rejects with `variant_required` (and, before that
   * guard, silently charged the parent's price against the parent's stock).
   * Send the buyer to the storefront PDP, which has the variation picker.
   */
  chooseVariationHref?: string;
  /**
   * When provided, the card shows a "Buy now" button alongside "Add to cart"
   * (kits) or replaces it. Omitted where everything should go through the cart.
   */
  onBuyNow?: () => void;
}) {
  const brand = useBrand();
  const discountPct = view.discountPct ?? 0;
  const hasDiscount = discountPct > 0 && !!view.priceBefore;
  // Kits flow as instant Buy Now (no cart). Non-kits: Buy Now is the primary
  // action (jumps to checkout) and Add to Cart remains the secondary.
  const showBuyNow = !!onBuyNow;
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <Link to={`${basePath}/${view.id}`} className="relative block">
        <ProductImage product={view} className="aspect-square" />
        <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
          {view.isKit && <Badge>{brand.kitLabelCap}</Badge>}
          {view.featured && (
            <Badge variant="secondary" className="text-xs">
              <Sparkles className="h-3 w-3" /> Featured
            </Badge>
          )}
          {hasDiscount && (
            <Badge variant="outline" className="border-success/40 bg-background text-success">
              −{discountPct.toFixed(0)}%
            </Badge>
          )}
        </div>
      </Link>
      <CardContent className="flex flex-1 flex-col p-4">
        <Link to={`${basePath}/${view.id}`} className="block hover:underline">
          <div className="line-clamp-2 font-medium leading-tight">{view.name}</div>
          <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{view.sku}</div>
        </Link>
        {view.description ? (
          <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{view.description}</p>
        ) : null}

        {/* Footer pinned to the bottom so price · PV · button align across every
            card, regardless of name length, description, or whether PV exists. */}
        <div className="mt-auto pt-3">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-semibold tabular-nums">{fmtCurrency(view.price)}</span>
            {hasDiscount ? (
              <span className="text-xs text-muted-foreground line-through tabular-nums">
                {fmtCurrency(view.priceBefore!)}
              </span>
            ) : null}
          </div>
          {/* Reserved line so cards with/without PV keep the same footer height. */}
          <div className="mt-0.5 h-4 text-xs text-muted-foreground">
            {Number(view.pv) > 0 ? `PV ${Number(view.pv).toFixed(0)}` : null}
          </div>
          {chooseVariationHref ? (
            <Button asChild variant="outline" className="mt-3 w-full" size="sm">
              <Link to={chooseVariationHref}>Choose variation</Link>
            </Button>
          ) : showBuyNow ? (
            // Non-kits: Buy Now + Add to Cart side-by-side.
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button onClick={onBuyNow} size="sm" disabled={pending}>
                Buy now
              </Button>
              <Button onClick={onAdd} variant="outline" size="sm">
                {inCart > 0 ? `In cart (${inCart})` : 'Add to cart'}
              </Button>
            </div>
          ) : (
            <Button onClick={onAdd} variant="outline" className="mt-3 w-full" size="sm">
              <ShoppingCart className="h-4 w-4" />
              {inCart > 0 ? `In cart (${inCart}) · Add more` : 'Add to cart'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
